/**
 * E013-S003-T007: Notion ZIP Pre-Parser
 *
 * Parses a Notion HTML export ZIP file to extract structure information
 * for preview before actual import.
 */

import * as fflate from 'fflate';

import type {
  ImportPreview,
  ImportTreeNode,
  NotionPageIcon,
  RelationPreview,
} from './types';

/**
 * Regex to extract Notion UUID from filename
 * Format: "Name abc123def456789.html" or "Name%20abc123def456789.html"
 */
const NOTION_UUID_REGEX = /([a-f0-9]{32})\.html$/i;

/**
 * Regex to detect table/database in HTML
 */
const DATABASE_TABLE_REGEX = /<table[^>]*class="[^"]*collection[^"]*"/i;

/**
 * Regex to detect relation links in table cells
 * Format: <a href="path/Name%20UUID.html">Display Name</a>
 */
const RELATION_LINK_REGEX =
  /<td[^>]*>[\s\S]*?<a\s+href="([^"]+\.html)"[^>]*>([^<]+)<\/a>/gi;

/**
 * Regex to extract page icon from Notion HTML
 */
const PAGE_ICON_REGEX =
  /<span[^>]*class="[^"]*page-header-icon[^"]*"[^>]*>([^<]+)<\/span>/i;
const PAGE_ICON_IMG_REGEX =
  /<img[^>]*class="[^"]*icon[^"]*"[^>]*src="([^"]+)"[^>]*>/i;

/**
 * Parse a Notion export ZIP file and extract preview information
 */
export async function parseNotionZip(zipFile: File): Promise<ImportPreview> {
  const unzipped = fflate.unzipSync(
    new Uint8Array(await zipFile.arrayBuffer())
  );

  // Build tree structure from paths
  const rootNode: ImportTreeNode = {
    name: zipFile.name.replace(/\.zip$/i, ''),
    path: '',
    type: 'folder',
    children: [],
  };

  const databases: ImportTreeNode[] = [];
  const relations: RelationPreview[] = [];
  const relationCounts = new Map<string, Map<string, number>>();

  let totalPages = 0;
  let totalRows = 0;

  // First pass: Build tree structure
  const paths = Object.keys(unzipped).filter(
    path => !path.includes('__MACOSX') && !path.includes('DS_Store')
  );

  // Sort paths for consistent tree building
  paths.sort();

  // Pre-scan: Find all database files to identify row-page folders
  // A database file is "Name UUID.html" with a matching folder "Name UUID/"
  const databasePaths = new Set<string>();
  for (const path of paths) {
    if (path.endsWith('.html')) {
      const basePath = path.replace(/\.html$/, '');
      // Check if there's a matching folder (by checking if any path starts with basePath/)
      const hasFolder = paths.some(p => p.startsWith(basePath + '/'));
      if (hasFolder) {
        databasePaths.add(basePath + '/');
      }
    }
  }

  for (const path of paths) {
    if (path.endsWith('/')) continue; // Skip directory entries

    const content = unzipped[path];
    const isHtml = path.endsWith('.html');

    if (isHtml) {
      // Check if this HTML is inside a database folder (= it's a row page)
      const isRowPage = Array.from(databasePaths).some(dbPath =>
        path.startsWith(dbPath)
      );

      if (!isRowPage) {
        totalPages++;
      }
      // Row pages are counted via database row counting, not as individual pages

      // Skip adding row pages to the tree - they're part of their database
      if (isRowPage) {
        continue;
      }

      // Decode content for analysis
      const htmlContent = new TextDecoder('utf-8').decode(content);

      // Check if this is a database
      const isDatabase = DATABASE_TABLE_REGEX.test(htmlContent);

      // Extract Notion UUID
      const uuidMatch = path.match(NOTION_UUID_REGEX);
      const notionId = uuidMatch ? uuidMatch[1] : undefined;

      // Extract icon
      const icon = extractPageIcon(htmlContent);

      // Count rows if database
      let rowCount: number | undefined;
      let columns: string[] | undefined;

      if (isDatabase) {
        const { rows, cols } = countDatabaseRows(htmlContent);
        rowCount = rows;
        columns = cols;
        totalRows += rows;

        // Detect relations
        const detectedRelations = detectRelations(htmlContent, path);
        for (const rel of detectedRelations) {
          if (!relationCounts.has(rel.source)) {
            relationCounts.set(rel.source, new Map());
          }
          const targetMap = relationCounts.get(rel.source)!;
          targetMap.set(rel.target, (targetMap.get(rel.target) || 0) + 1);
        }
      }

      // Add to tree
      addToTree(
        rootNode,
        path,
        isDatabase ? 'database' : 'page',
        icon,
        notionId,
        rowCount,
        columns
      );

      if (isDatabase) {
        // Find the node we just added and add to databases list
        const node = findNodeByPath(rootNode, path);
        if (node) {
          databases.push(node);
        }
      }
    }
  }

  // Convert relation counts to RelationPreview array
  for (const [source, targets] of relationCounts) {
    for (const [target, count] of targets) {
      relations.push({
        sourceDatabase: source,
        targetDatabase: target,
        linkCount: count,
      });
    }
  }

  return {
    rootNode,
    databases,
    relations,
    totalPages,
    totalDatabases: databases.length,
    totalRows,
  };
}

/**
 * Add a file path to the tree structure
 */
function addToTree(
  root: ImportTreeNode,
  filePath: string,
  type: 'page' | 'database',
  icon?: NotionPageIcon,
  notionId?: string,
  rowCount?: number,
  columns?: string[]
): void {
  const parts = filePath.split('/').filter(p => p.length > 0);
  let current = root;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const isLast = i === parts.length - 1;

    if (isLast) {
      // This is the file - extract display name
      const displayName = extractDisplayName(part);

      current.children.push({
        name: displayName,
        path: filePath,
        type,
        children: [],
        icon,
        notionId,
        rowCount,
        columns,
      });
    } else {
      // This is a folder
      let folder = current.children.find(
        c => c.type === 'folder' && c.name === part
      );

      if (!folder) {
        folder = {
          name: part,
          path: parts.slice(0, i + 1).join('/'),
          type: 'folder',
          children: [],
        };
        current.children.push(folder);
      }

      current = folder;
    }
  }
}

/**
 * Find a node by its path
 */
function findNodeByPath(
  root: ImportTreeNode,
  path: string
): ImportTreeNode | null {
  if (root.path === path) return root;

  for (const child of root.children) {
    const found = findNodeByPath(child, path);
    if (found) return found;
  }

  return null;
}

/**
 * Extract display name from filename
 * "Name abc123def456789.html" -> "Name"
 */
function extractDisplayName(filename: string): string {
  // Remove .html extension
  let name = filename.replace(/\.html$/i, '');

  // URL decode
  try {
    name = decodeURIComponent(name);
  } catch {
    // Keep as-is if decode fails
  }

  // Remove UUID suffix (32 hex chars at end)
  name = name.replace(/\s+[a-f0-9]{32}$/i, '');

  return name || filename;
}

/**
 * Extract page icon from HTML content
 */
function extractPageIcon(html: string): NotionPageIcon | undefined {
  // Try emoji icon first
  const emojiMatch = html.match(PAGE_ICON_REGEX);
  if (emojiMatch && emojiMatch[1]) {
    return {
      type: 'emoji',
      content: emojiMatch[1].trim(),
    };
  }

  // Try image icon
  const imgMatch = html.match(PAGE_ICON_IMG_REGEX);
  if (imgMatch && imgMatch[1]) {
    return {
      type: 'image',
      content: imgMatch[1],
    };
  }

  return undefined;
}

/**
 * Count rows in a database table
 */
function countDatabaseRows(html: string): { rows: number; cols: string[] } {
  // Count <tr> tags in <tbody>
  const tbodyMatch = html.match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i);
  if (!tbodyMatch) return { rows: 0, cols: [] };

  const rowMatches = tbodyMatch[1].match(/<tr[^>]*>/gi);
  const rows = rowMatches ? rowMatches.length : 0;

  // Extract column names from <thead>
  const cols: string[] = [];
  const theadMatch = html.match(/<thead[^>]*>([\s\S]*?)<\/thead>/i);
  if (theadMatch) {
    const thMatches = theadMatch[1].matchAll(/<th[^>]*>[\s\S]*?<\/th>/gi);
    for (const th of thMatches) {
      // Extract text content from th
      const textMatch = th[0].match(/>([^<]+)</);
      if (textMatch) {
        cols.push(textMatch[1].trim());
      }
    }
  }

  return { rows, cols };
}

/**
 * Detect relation links in database HTML
 */
function detectRelations(
  html: string,
  sourcePath: string
): Array<{ source: string; target: string }> {
  const relations: Array<{ source: string; target: string }> = [];

  // Find all links in table cells
  let match;
  while ((match = RELATION_LINK_REGEX.exec(html)) !== null) {
    const href = match[1];

    // Check if this links to another HTML file (potential relation)
    if (href.endsWith('.html')) {
      // Resolve relative path
      const sourceParts = sourcePath.split('/');
      sourceParts.pop(); // Remove filename

      const hrefParts = href.split('/');
      let targetPath = [...sourceParts];

      for (const part of hrefParts) {
        if (part === '..') {
          targetPath.pop();
        } else if (part !== '.') {
          targetPath.push(part);
        }
      }

      // URL decode the target path
      try {
        const targetPathStr = decodeURIComponent(targetPath.join('/'));
        relations.push({
          source: sourcePath,
          target: targetPathStr,
        });
      } catch {
        // Keep encoded if decode fails
        relations.push({
          source: sourcePath,
          target: targetPath.join('/'),
        });
      }
    }
  }

  return relations;
}

/**
 * Calculate how many docs would be created at a given aggregation level
 */
export function calculateDocsAtLevel(
  preview: ImportPreview,
  level: 0 | 1 | 2
): number {
  switch (level) {
    case 0:
      // All in one doc
      return 1;
    case 1:
      // One doc per top-level item
      return preview.rootNode.children.length || 1;
    case 2:
      // One doc per page (current default)
      return preview.totalPages;
  }
}

/**
 * Get description for aggregation level
 */
export function getLevelDescription(
  preview: ImportPreview,
  level: 0 | 1 | 2
): string {
  const docCount = calculateDocsAtLevel(preview, level);

  switch (level) {
    case 0:
      return `${docCount} Doc - All ${preview.totalDatabases} databases on same canvas`;
    case 1:
      return `${docCount} Docs - One per top-level folder`;
    case 2:
      return `${docCount} Docs - One per page (default)`;
  }
}
